import React from 'react';
import { X, Calendar, Users, Target, CheckCircle, Clock, AlertCircle, Star, MessageSquare, XCircle } from 'lucide-react';
import Button from './Button/Button';
import { formatDate } from '../../utils/stringUtils';

const ReviewViewModal = ({ review, isOpen, onClose, isDrawer }) => {
  if (!isOpen || !review) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "draft":
        return <Clock className="h-4 w-4 text-gray-500" />;
      case "in_progress":
        return <Target className="h-4 w-4 text-blue-500" />;
      case "submitted":
        return <CheckCircle className="h-4 w-4 text-yellow-500" />;
      case "under_review":
        return <AlertCircle className="h-4 w-4 text-purple-500" />;
      case "sent_back":
        return <MessageSquare className="h-4 w-4 text-orange-500" />;
      case "overdue":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case "cancelled":
        return <Clock className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "approved":
        return "text-green-600 bg-green-100";
      case "rejected":
        return "text-red-600 bg-red-100";
      case "draft":
        return "text-gray-600 bg-gray-100";
      case "in_progress":
        return "text-blue-600 bg-blue-100";
      case "submitted":
        return "text-yellow-600 bg-yellow-100";
      case "under_review":
        return "text-purple-600 bg-purple-100";
      case "sent_back":
        return "text-orange-600 bg-orange-100";
      case "overdue":
        return "text-red-600 bg-red-100";
      case "cancelled":
        return "text-gray-600 bg-gray-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getReviewTypeDisplay = (reviewType) => {
    switch (reviewType) {
      case "self":
        return "Self Review";
      case "manager":
        return "Manager Review";
      case "peer":
        return "Peer Review";
      default:
        return "Performance Review";
    }
  };

  // Render star rating with support for decimal values
  const renderStarRating = (rating) => {
    if (rating === undefined || rating === null) return null;

    const ratingValue = parseFloat(rating);
    if (isNaN(ratingValue)) return null;

    const fullStars = Math.floor(ratingValue);
    const hasPartialStar = ratingValue % 1 !== 0;
    const partialPercentage = (ratingValue % 1) * 100;

    return (
      <div className="flex items-center space-x-0.5">
        {[...Array(5)].map((_, index) => {
          if (index < fullStars) {
            // Full star
            return (
              <Star
                key={index}
                className="h-4 w-4 text-yellow-400 fill-yellow-400"
              />
            );
          } else if (index === fullStars && hasPartialStar) {
            // Partial star - gray background with yellow overlay
            return (
              <div key={index} className="relative h-4 w-4 inline-flex items-center">
                {/* Gray background star */}
                <Star className="h-4 w-4 text-gray-300 absolute" />
                {/* Yellow partial star with overflow clip */}
                <div
                  className="absolute left-0 top-0 overflow-hidden"
                  style={{ width: `${partialPercentage}%` }}
                >
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                </div>
              </div>
            );
          } else {
            // Empty star
            return (
              <Star
                key={index}
                className="h-4 w-4 text-gray-300"
              />
            );
          }
        })}
        <span className="ml-1 text-sm text-gray-600">{ratingValue}</span>
      </div>
    );
  };

  const renderContent = () => (
    <div className="p-6">
      <div className="space-y-6">
        {/* Employee and Manager Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-gray-200">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Employee</label>
            <p className="text-base font-semibold text-gray-900 break-words capitalize">{review.employee || 'N/A'}</p>
            {review.employeeEmail && (
              <p className="text-sm text-gray-600 mt-1 break-words normal-case">{review.employeeEmail}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Manager</label>
            <p className="text-base font-semibold text-gray-900 break-words capitalize">{review.manager || 'N/A'}</p>
            {review.managerEmail && (
              <p className="text-sm text-gray-600 mt-1 break-words normal-case">{review.managerEmail}</p>
            )}
          </div>
        </div>

        {/* Total Goals and Goals Completed Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-gray-200">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Total Goals</label>
            <p className="text-2xl font-bold text-gray-900">{review.totalGoals || 0}</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Goals Completed</label>
            <p className="text-2xl font-bold text-gray-900">{review.goalsCompleted || 0}</p>
          </div>
        </div>

        {/* Rating and Status Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-gray-200">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Rating</label>
            {review.rating !== undefined && review.rating !== null ? (
              <div className="flex items-center">
                {renderStarRating(review.rating)}
              </div>
            ) : (
              <p className="text-2xl font-bold text-gray-900">N/A</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Status</label>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                review.status || "pending"
              )}`}
            >
              {getStatusIcon(review.status || "pending")}
              <span className="ml-2 capitalize">
                {review.status ? review.status.replace("_", " ") : "pending"}
              </span>
            </span>
          </div>
        </div>

        {/* Last Updated Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-gray-200">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Last Updated</label>
            <p className="text-base text-gray-900 break-words">
              {review.updatedAt ? formatDate(review.updatedAt, { includeTime: false }) : "N/A"}
            </p>
          </div>
        </div>

        {/* Created / Approved / Rejected By Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Created By</label>
            <p className="text-base font-semibold text-gray-900 break-words capitalize">
              {review.assignedByUser?.name || "Not available"}
            </p>
            <p className="text-sm text-gray-600 mt-1 break-words normal-case">
              {review.assignedByUser?.email || "—"}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {review.status === "rejected"
                ? "Rejected By"
                : review.status === "approved"
                  ? "Approved By"
                  : "Approval Status"}
            </label>
            <p className="text-base font-semibold text-gray-900 break-words capitalize">
              {review.status === "approved"
                ? review.approvedByUser?.name || "Not available"
                : review.status === "rejected"
                  ? review.rejectedByUser?.name || "Not available"
                  : "Pending"}
            </p>
            <p className="text-sm text-gray-600 mt-1 break-words normal-case">
              {review.status === "approved"
                ? review.approvedByUser?.email || "—"
                : review.status === "rejected"
                  ? review.rejectedByUser?.email || "—"
                  : "Awaiting action"}
            </p>
            {(review.status === "approved" && review.approvedAt) ||
              (review.status === "rejected" && review.rejectedAt) ? (
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(
                  review.status === "approved" ? review.approvedAt : review.rejectedAt,
                  { includeTime: false }
                )}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Reason Section */}
      {review.reason && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Reason</h3>
          <div>
            <p className="text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap break-words">
              {review.reason.charAt(0).toUpperCase() + review.reason.slice(1)}
            </p>
          </div>
        </div>
      )}

      {/* Comments Section */}
      {review.comments && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Comments</h3>
          <div>
            <p className="text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap break-words">
              {review.comments.charAt(0).toUpperCase() + review.comments.slice(1)}
            </p>
          </div>
        </div>
      )}


      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{review.totalGoals || 0}</div>
            <div className="text-sm text-gray-600">Total Goals</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{review.goalsCompleted || 0}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="text-center">
            {review.rating !== undefined && review.rating !== null ? (
              <div className="flex justify-center items-center">
                {renderStarRating(review.rating)}
              </div>
            ) : (
              <div className="text-2xl font-bold text-yellow-600">N/A</div>
            )}
            <div className="text-sm text-gray-600 mt-1">Rating</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isDrawer) {
    return renderContent();
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg shadow-xl w-[70%] mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Review Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        {renderContent()}

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-6"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewViewModal;
